import type { ReactNode } from "react";
import { ExternalLink } from "lucide-react";

import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";

/** Copy `text` to the clipboard, falling back to execCommand when the async
 *  Clipboard API is unavailable (insecure context, older browsers). */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

interface CopyableUrlProps {
  /** The URL to copy. When empty, the control is disabled and shows `fallback`. */
  url: string | null | undefined;
  /** Rendered when `url` is empty (e.g. `URL #123`). */
  fallback?: ReactNode;
  /** Extra classes merged onto the wrapper (e.g. margin, text size/colour per
   *  table). Text styles cascade to the URL label and the open-in-new-tab icon. */
  className?: string;
}

/** A URL rendered as a click-to-copy label plus an "open in new tab" icon.
 *  Used anywhere a URL string appears in a table so every URL is one click to
 *  copy and one click to open. */
export function CopyableUrl({
  url,
  fallback,
  className,
}: CopyableUrlProps): JSX.Element {
  const { toast } = useToast();

  async function onCopy(): Promise<void> {
    if (!url) return;
    const ok = await copyToClipboard(url);
    toast(
      ok
        ? { title: "URL скопійовано", variant: "success" }
        : { title: "Не вдалося скопіювати", variant: "destructive" },
    );
  }

  return (
    <div className={cn("flex min-w-0 items-center gap-1.5", className)}>
      <button
        type="button"
        onClick={onCopy}
        disabled={!url}
        title={url ? `${url}\n(клікніть, щоб скопіювати)` : undefined}
        className="min-w-0 cursor-pointer truncate text-left hover:underline disabled:cursor-default disabled:no-underline"
      >
        {url ?? fallback}
      </button>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Відкрити в новому вікні"
          aria-label="Відкрити в новому вікні"
          className="shrink-0 opacity-60 transition-opacity hover:opacity-100"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ) : null}
    </div>
  );
}
